import { MISSING_DATE_LABEL, toValidDate, type DateLike } from "@/lib/safeDate";

export type DueBucket = "overdue" | "today" | "tomorrow" | "later";

export const DUE_LABEL_KEYS: Record<DueBucket, string> = {
  overdue: "palmtree.due.overdue",
  today: "palmtree.due.today",
  tomorrow: "palmtree.due.tomorrow",
  later: "palmtree.due.later",
};

const MADRID = "Europe/Madrid";

export function madridCalendarDate(value: DateLike): string {
  const date = toValidDate(value);
  if (!date) return "";
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: MADRID,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(date);
}

export function madridClock(value: DateLike): string {
  const date = toValidDate(value);
  if (!date) return "";
  const parts = new Intl.DateTimeFormat("en-GB", {
    timeZone: MADRID,
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  }).formatToParts(date);
  const hour = parts.find((part) => part.type === "hour")?.value ?? "00";
  const minute = parts.find((part) => part.type === "minute")?.value ?? "00";
  return `${hour.padStart(2, "0")}:${minute.padStart(2, "0")}`;
}

function madridDayDiff(due: DateLike, now: Date): number {
  const dueDay = madridCalendarDate(due);
  if (!dueDay) return Number.NaN;
  const dueMs = Date.parse(`${dueDay}T12:00:00.000Z`);
  const todayMs = Date.parse(`${madridCalendarDate(now)}T12:00:00.000Z`);
  return Math.round((todayMs - dueMs) / 86_400_000);
}

export function dutchRelativeDayMadrid(due: DateLike, now = new Date()) {
  if (!toValidDate(due)) return MISSING_DATE_LABEL;
  const diff = madridDayDiff(due, now);
  if (diff === 0) return "vandaag";
  if (diff === 1) return "gisteren";
  if (diff === -1) return "morgen";
  if (diff > 1 && diff < 7) return `${diff} dagen geleden`;
  if (diff >= 7 && diff < 14) return "1 week geleden";
  const [year, month, day] = madridCalendarDate(due).split("-").map(Number);
  return new Intl.DateTimeFormat("nl-NL", {
    day: "numeric",
    month: "short",
    timeZone: "UTC",
  })
    .format(new Date(Date.UTC(year, month - 1, day)))
    .replace(/\./g, "");
}

export function dueBucketMadrid(due: DateLike, now = new Date()): DueBucket {
  if (!toValidDate(due)) return "later";
  const diff = -madridDayDiff(due, now);
  if (diff < 0) return "overdue";
  if (diff === 0) return "today";
  if (diff === 1) return "tomorrow";
  return "later";
}

export const startOfLocalDay = (d = new Date()) => {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
};

export const dueBucket = (dueDate: DateLike, now = new Date()): DueBucket => {
  const parsed = toValidDate(dueDate);
  if (!parsed) return "later";
  const due = startOfLocalDay(parsed);
  const today = startOfLocalDay(now);
  const diff = Math.round((due.getTime() - today.getTime()) / 86_400_000);
  if (diff < 0) return "overdue";
  if (diff === 0) return "today";
  if (diff === 1) return "tomorrow";
  return "later";
};

export const dutchRelativeDay = (date: DateLike, now = new Date()) => {
  const parsed = toValidDate(date);
  if (!parsed) return MISSING_DATE_LABEL;
  const diff = Math.round(
    (startOfLocalDay(now).getTime() - startOfLocalDay(parsed).getTime()) /
      86_400_000,
  );
  if (diff === 0) return "vandaag";
  if (diff === 1) return "gisteren";
  if (diff === -1) return "morgen";
  if (diff > 1 && diff < 7) return `${diff} dagen geleden`;
  if (diff >= 7 && diff < 14) return "1 week geleden";
  return new Intl.DateTimeFormat("nl-NL", {
    day: "numeric",
    month: "short",
  }).format(parsed);
};

export const dutchTodayHeading = (now = new Date()) =>
  new Intl.DateTimeFormat("nl-NL", {
    weekday: "long",
    day: "numeric",
    month: "short",
  }).format(now);

export const isOverdueOrToday = (dueDate: DateLike, now = new Date()) => {
  const bucket = dueBucket(dueDate, now);
  return bucket === "overdue" || bucket === "today";
};
